import type { Camada, CamadaLiquidacionResumen, Sexo } from './types';

export interface CamadaLiquidacionListado extends Camada {
  liquidacion: { total_eur: string; fecha_guardado: string; local_pending?: boolean } | null;
}

export interface TramoBajas {
  edad_min: number;
  edad_max: number | null;
  porcentaje: string;
}
export interface ParametrosSexo {
  base: string;
  complemento: string;
  coef_dias: string;
  coef_conversion: string;
  coef_bajas: string;
  tramos: TramoBajas[];
}
export interface ConfiguracionLiquidacion {
  sexos: Record<Sexo, ParametrosSexo>;
  porcentaje_iva: string;
  ajuste_carga_predeterminado_eur: string;
  bonificacion_predeterminada_eur: string;
}
export interface DatosPreparacion {
  sexo: Sexo;
  fecha_entrada: string;
  fecha_final: string | null;
  edad_entrada: number | null;
  aves_entradas: number;
  bajas_registradas: number;
  kg_entrada: string;
  kg_salida: string | null;
  kg_pienso: string | null;
  ajuste_carga_eur: string;
  bonificacion_eur: string;
}
export interface DatosLiquidacion extends Omit<
  DatosPreparacion,
  'fecha_final' | 'edad_entrada' | 'kg_salida' | 'kg_pienso'
> {
  fecha_final: string;
  edad_entrada: number;
  kg_salida: string;
  kg_pienso: string;
}
export interface ResultadoLiquidacion extends CamadaLiquidacionResumen {
  sexo: Sexo;
  dias_engorde: number;
  edad_total: number;
  kg_ganados: string;
  toneladas_ganadas: string;
  conversion: string;
  porcentaje_bajas: string;
  porcentaje_referencia: string;
  tramo_aplicado: TramoBajas;
}
export interface LiquidacionSnapshot {
  datos: DatosLiquidacion;
  fuentes: DatosPreparacion;
  configuracion: ConfiguracionLiquidacion;
  resultado: ResultadoLiquidacion;
}
export interface LiquidacionGuardada {
  version: number;
  updated_at: string;
  snapshot: LiquidacionSnapshot;
}
export interface LiquidacionPreparacion {
  datos: DatosPreparacion;
  configuracion: ConfiguracionLiquidacion;
  guardada: LiquidacionGuardada | null;
  cerrada: boolean;
  local_pending?: boolean;
}
export type ModoLiquidacion = 'nueva' | 'guardada' | 'actualizar_registros';
export type LiquidacionCambios = Partial<
  Pick<
    DatosLiquidacion,
    | 'fecha_final'
    | 'edad_entrada'
    | 'kg_entrada'
    | 'kg_salida'
    | 'kg_pienso'
    | 'ajuste_carga_eur'
    | 'bonificacion_eur'
  >
>;
export interface LiquidacionSolicitud {
  modo: ModoLiquidacion;
  cambios: LiquidacionCambios;
  token_preview?: string | null;
  version_esperada?: number;
  approved_snapshot?: LiquidacionSnapshot;
}
export interface LiquidacionPreview extends LiquidacionSnapshot {
  token_preview: string | null;
  version_esperada: number;
  local_preview?: boolean;
}

/** Presentación HALF_UP decimal exacta. No calcula importes económicos. */
export function formatoDecimal(value: string | number | null | undefined, places = 2): string {
  if (value == null) return '—';
  const match = /^([+-]?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(String(value));
  if (!match) return '—';
  const exponent = Number(match[4] ?? 0);
  if (!Number.isInteger(exponent) || Math.abs(exponent) > 200) return '—';
  const fraction = match[3] ?? '';
  let units = BigInt(match[2] + fraction);
  const shift = places + exponent - fraction.length;
  if (shift >= 0) units *= 10n ** BigInt(shift);
  else {
    const divisor = 10n ** BigInt(-shift);
    units = units / divisor + ((units % divisor) * 2n >= divisor ? 1n : 0n);
  }
  const digits = units.toString().padStart(places + 1, '0');
  const integer = places ? digits.slice(0, -places) : digits;
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${match[1] === '-' && units !== 0n ? '-' : ''}${grouped}${places ? ',' + digits.slice(-places) : ''}`;
}
export function formatoEuros(value: string): string {
  return `${formatoDecimal(value)} €`;
}

/** Los campos económicos admiten hasta doce decimales; conserva su valor. */
export function entradaDecimal(value: string | number | null): string {
  if (value == null) return '';
  const formatted = formatoDecimal(value, 12).replaceAll('.', '');
  return formatted.replace(/0+$/, '').replace(/,$/, '');
}
export function decimalSolicitud(value: string): string {
  return value.trim().includes(',')
    ? value.trim().replaceAll('.', '').replace(',', '.')
    : value.trim();
}
